import i18n from 'modules/core/lib/i18n'

import {
  CostCenterEmployeeWithStructuredAssignment,
  StructuredAssignment,
} from 'modules/cost_centers/components/Resources/Employees/types'

export type TextValue = {
  text?: string | null
  placeholder?: string
}

// Determine which column is the "last" (deepest level) in the job structure hierarchy
// Returns: 'family' | 'function' | 'role' | null
export const getLastJobStructureLevel = (
  item: CostCenterEmployeeWithStructuredAssignment
): 'family' | 'function' | 'role' | null => {
  const edges = item.structuredAssignments.edges
  if (edges.length === 0) return null

  // Helper to check if a value is non-empty (not null, undefined, or empty string)
  const hasValue = (value: string | null | undefined): boolean => !!value

  // Check if any assignment has role data
  const hasRoles = edges.some((edge) => hasValue(edge.node.jobCatalogRoleName))
  if (hasRoles) return 'role'

  // Check if any assignment has function data
  const hasFunctions = edges.some((edge) => hasValue(edge.node.jobCatalogFunctionName))
  if (hasFunctions) return 'function'

  // Check if any assignment has family data
  const hasFamilies = edges.some((edge) => hasValue(edge.node.jobCatalogFamilyName))
  if (hasFamilies) return 'family'

  return null
}

//****** PARENT LABELS ******//
export const familyLabelForParent = (
  item: CostCenterEmployeeWithStructuredAssignment
): TextValue | undefined => {
  const lastLevel = getLastJobStructureLevel(item)
  const edges = item.structuredAssignments.edges

  // If family is the last level, show count of assignments
  if (lastLevel === 'family') {
    return {
      text: edges.length.toString(),
    }
  }

  // For non-last columns, show the actual name (first assignment's family name)
  // This keeps consistency in the parent row - names for intermediate levels
  const firstFamilyName = edges[0]?.node.jobCatalogFamilyName
  if (firstFamilyName) {
    return { text: firstFamilyName }
  }

  return {
    placeholder: i18n.t('finance.cost_centers_list.headers.job_catalog.not_applicable'),
  }
}

export const functionLabelForParent = (
  item: CostCenterEmployeeWithStructuredAssignment
): TextValue | undefined => {
  const lastLevel = getLastJobStructureLevel(item)
  const edges = item.structuredAssignments.edges

  // If function is the last level, show count of assignments
  if (lastLevel === 'function') {
    return {
      text: edges.length.toString(),
    }
  }

  // If family is the last level, function doesn't apply
  if (lastLevel === 'family') {
    return {
      placeholder: i18n.t('finance.cost_centers_list.headers.job_catalog.not_applicable'),
    }
  }

  // For non-last columns (when role is the last), show the actual name
  const firstFunctionName = edges[0]?.node.jobCatalogFunctionName
  if (firstFunctionName) {
    return { text: firstFunctionName }
  }

  return {
    placeholder: i18n.t('finance.cost_centers_list.headers.job_catalog.not_applicable'),
  }
}

export const roleLabelForParent = (
  item: CostCenterEmployeeWithStructuredAssignment
): TextValue | undefined => {
  const lastLevel = getLastJobStructureLevel(item)
  const edges = item.structuredAssignments.edges

  // If role is the last level, show count of assignments
  if (lastLevel === 'role') {
    return {
      text: edges.length.toString(),
    }
  }

  // If family or function is the last level, role doesn't apply
  if (lastLevel === 'family' || lastLevel === 'function') {
    return {
      placeholder: i18n.t('finance.cost_centers_list.headers.job_catalog.not_applicable'),
    }
  }

  return {
    placeholder: i18n.t('finance.cost_centers_list.headers.job_catalog.not_applicable'),
  }
}

//****** CHILD LABELS ******//
// Helper to get the deepest available name for a child assignment
const getDeepestName = (item: StructuredAssignment): string | null => {
  if (item.jobCatalogRoleName) return item.jobCatalogRoleName
  if (item.jobCatalogFunctionName) return item.jobCatalogFunctionName
  if (item.jobCatalogFamilyName) return item.jobCatalogFamilyName
  return null
}

// For child rows, show the actual name in the "last" column (as determined by parent)
// If the child doesn't have data at the parent's last level, show its deepest available name
export const familyLabelForChild = (
  item: StructuredAssignment,
  parent: CostCenterEmployeeWithStructuredAssignment
): TextValue | undefined => {
  const lastLevel = getLastJobStructureLevel(parent)

  // Family column shows the name only if family is the last level
  if (lastLevel === 'family') {
    const name = item.jobCatalogFamilyName || getDeepestName(item)
    if (name) {
      return { text: name }
    }
  }

  // Otherwise N/A
  return {
    placeholder: i18n.t('finance.cost_centers_list.headers.job_catalog.not_applicable'),
  }
}

export const functionLabelForChild = (
  item: StructuredAssignment,
  parent: CostCenterEmployeeWithStructuredAssignment
): TextValue | undefined => {
  const lastLevel = getLastJobStructureLevel(parent)

  // Function column shows the name if function is the last level
  if (lastLevel === 'function') {
    // Try to get function name, or fall back to deepest available
    const name = item.jobCatalogFunctionName || getDeepestName(item)
    if (name) {
      return { text: name }
    }
  }

  // Otherwise N/A
  return {
    placeholder: i18n.t('finance.cost_centers_list.headers.job_catalog.not_applicable'),
  }
}

export const roleLabelForChild = (
  item: StructuredAssignment,
  parent: CostCenterEmployeeWithStructuredAssignment
): TextValue | undefined => {
  const lastLevel = getLastJobStructureLevel(parent)

  // Role column shows the name if role is the last level
  if (lastLevel === 'role') {
    // Try to get role name, or fall back to deepest available
    const name = item.jobCatalogRoleName || getDeepestName(item)
    if (name) {
      return { text: name }
    }
  }

  // Otherwise N/A
  return {
    placeholder: i18n.t('finance.cost_centers_list.headers.job_catalog.not_applicable'),
  }
}
